// Bounds affect displayed row extrema only, never beats, averages or graph bars.
export function initialHrBounds(rowStats = []) {
  const minima = rowStats.map((s) => s.min).filter(Number.isFinite);
  const maxima = rowStats.map((s) => s.max).filter(Number.isFinite);
  return {
    minFloor: minima.length ? String(Math.round(Math.min(...minima))) : '',
    maxCeiling: maxima.length ? String(Math.round(Math.max(...maxima))) : '',
  };
}
export function boundedRowStats(rowStats = [], bounds = {}) {
  const defaults = initialHrBounds(rowStats);
  const value = (key) => {
    const raw = bounds[key] ?? defaults[key];
    if (raw === '' && defaults[key] === '') return null;
    if (
      String(raw).trim() === '' ||
      !Number.isFinite(Number(raw)) ||
      Number(raw) < 0 ||
      !Number.isInteger(Number(raw))
    ) {
      throw new Error('Enter whole, nonnegative BPM values for Min Floor and Max Ceiling.');
    }
    return Number(raw);
  };
  const floor = value('minFloor');
  const ceiling = value('maxCeiling');
  if (floor !== null && ceiling !== null && floor > ceiling)
    throw new Error('Min Floor cannot exceed Max Ceiling.');
  // Round consistently with the report's whole-BPM labels before bounding.
  const clamp = (v) =>
    v === null || v === undefined
      ? null
      : Math.max(floor ?? -Infinity, Math.min(ceiling ?? Infinity, Math.round(v)));
  const result = rowStats.map((s) => ({ ...s, min: clamp(s.min), max: clamp(s.max) }));
  const valid = result.filter((s) => Number.isFinite(s.min) && Number.isFinite(s.max));
  if (!valid.length) return result;
  const lowest = valid.reduce((best, s) => (s.min < best.min ? s : best));
  const highest = valid.reduce((best, s) => (s.max > best.max ? s : best));
  if (floor !== null) lowest.min = floor;
  if (ceiling !== null) highest.max = ceiling;
  return result;
}

// Clamp every row within the selected bounds, then preserve the entered floor and ceiling once.
export function reconcileRowExtremes(rows, limits) {
  const result = rows.map((row) => ({ ...row }));
  const valid = result.filter((row) => Number.isFinite(row.min) || Number.isFinite(row.max));
  if (!valid.length) throw new Error('No graph HR values are available.');
  if (
    !Number.isInteger(limits.min) ||
    !Number.isInteger(limits.max) ||
    limits.min < 0 ||
    limits.min > limits.max
  )
    throw new Error('Invalid HR values.');
  const clamp = (value) =>
    Number.isFinite(value) ? Math.max(limits.min, Math.min(limits.max, value)) : value;
  const clamped = result.map((row) => ({
    ...row,
    min: clamp(row.min),
    max: clamp(row.max),
  }));
  const low = clamped
    .filter((row) => Number.isFinite(row.min))
    .reduce((best, row) => (row.min < best.min ? row : best));
  const high = clamped
    .filter((row) => Number.isFinite(row.max))
    .reduce((best, row) => (row.max > best.max ? row : best));
  low.min = limits.min;
  high.max = limits.max;
  return clamped;
}

// A saved stitch adjustment is valid only while its values match the controls and include both entered bounds.
export function displayedRowStats(rowStats = [], meta = {}) {
  const bounded = boundedRowStats(rowStats, meta);
  const defaults = initialHrBounds(rowStats);
  const floor = Number(meta.minFloor ?? defaults.minFloor);
  const ceiling = Number(meta.maxCeiling ?? defaults.maxCeiling);
  const withinBounds = (row) => {
    if (Number.isFinite(row.min) && row.min < floor) return false;
    if (Number.isFinite(row.max) && row.max > ceiling) return false;
    return true;
  };
  if (
    meta.stitchRowStats?.length === rowStats.length &&
    meta.stitchLimits?.min === floor &&
    meta.stitchLimits?.max === ceiling &&
    meta.stitchRowStats.every(withinBounds) &&
    meta.stitchRowStats.some((row) => row.min === floor) &&
    meta.stitchRowStats.some((row) => row.max === ceiling)
  ) {
    return meta.stitchRowStats.map((row) => ({ ...row }));
  }
  return bounded;
}
export function alignedGraphMetadata(rowStats, meta, limits) {
  return {
    ...meta,
    minFloor: String(limits.min),
    maxCeiling: String(limits.max),
    stitchLimits: { min: limits.min, max: limits.max },
    stitchRowStats: reconcileRowExtremes(displayedRowStats(rowStats, meta), limits),
  };
}
